import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import EditOutlined from "@mui/icons-material/EditOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import { MOC_TABLE_COLUMNS, MocRow } from "../../data/moc.data";
import {
  useGetMocsQuery,
  useLazyGetMocDocumentAccessDetailsQuery
} from "../../data/api/queries/getMocs";
import { ToastMessageUtil } from "common/components/base/toast-message/ToastMessage.util";
import { ToastVariant } from "common/enums/controls/toast.enum";
import useOnClickOutside from "common/hooks/useOnClickOutside";
import MocDetailDrawer from "./components/MocDetailDrawer";
import "./ManagementOfChange.scss";

const PAGE_SIZE = 10;

const ManagementOfChange: React.FC = () => {
  const [search, setSearch] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRow, setSelectedRow] = useState<MocRow | null>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { data: mocData, isLoading, isFetching, refetch } = useGetMocsQuery();
  const [getMocDocumentAccessDetails] =
    useLazyGetMocDocumentAccessDetailsQuery();

  useOnClickOutside(searchRef, () => setShowSuggestions(false));

  // When redirected here from a duplicate MOC prompt, pre-fill the search with
  // the existing MOC id so the user lands straight on the matching record.
  useEffect(() => {
    const state = location.state as { searchMocId?: string } | null;
    if (state?.searchMocId) {
      setSearch(state.searchMocId);
    }
  }, [location.state]);

  const kpis = mocData?.kpis ?? [];
  const rows = useMemo<MocRow[]>(() => mocData?.rows ?? [], [mocData]);

  const filteredRows = useMemo<MocRow[]>(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return rows;
    }
    return rows.filter((row) =>
      [row.mocId, row.title, row.asset, row.owner, row.stage]
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [search, rows]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));

  // Reset to first page whenever the filter result set changes size/order
  useEffect(() => {
    setCurrentPage(1);
  }, [search, rows]);

  // Keep current page within bounds
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedRows = useMemo<MocRow[]>(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredRows.slice(start, start + PAGE_SIZE);
  }, [filteredRows, currentPage]);

  const rangeStart =
    filteredRows.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * PAGE_SIZE, filteredRows.length);

  // Windowed page numbers (max 3 visible), always including current page
  const pageNumbers = useMemo<number[]>(() => {
    const maxVisible = 3;
    let start = Math.max(1, currentPage - 1);
    const end = Math.min(totalPages, start + maxVisible - 1);
    start = Math.max(1, end - maxVisible + 1);
    const pages: number[] = [];
    for (let page = start; page <= end; page += 1) {
      pages.push(page);
    }
    return pages;
  }, [currentPage, totalPages]);

  const goToPage = (page: number) => {
    setCurrentPage(Math.min(Math.max(page, 1), totalPages));
  };

  // MOC-ID suggestions for the search box (unique IDs matching the query)
  const mocIdSuggestions = useMemo<MocRow[]>(() => {
    const query = search.trim().toLowerCase();
    const seen = new Set<string>();
    return rows.filter((row) => {
      if (seen.has(row.mocId)) {
        return false;
      }
      const matches = !query || row.mocId.toLowerCase().includes(query);
      if (matches) {
        seen.add(row.mocId);
        return true;
      }
      return false;
    });
  }, [search, rows]);

  const handleSelectMocId = (mocId: string) => {
    setSearch(mocId);
    setShowSuggestions(false);
  };

  const handleClearSearch = () => {
    setSearch("");
    setShowSuggestions(false);
  };

  // Map the workflow stage to the step the MOC should open on.
  const getInitialStepForStage = (stage?: string): number | undefined => {
    const normalized = (stage || "").toUpperCase().replace(/\s+/g, "_");
    if (
      normalized.includes("DECISION_PACKAGE") ||
      normalized.includes("PACKAGE_GENERATED") ||
      normalized.includes("MOC_COMPLETED")
    ) {
      return 3; // Decision Package & Review
    }
    if (normalized.includes("ASSESSMENT_COMPLETED")) {
      return 2; // Assessment Workspace
    }
    if (normalized.includes("ANALYSIS_COMPLETED")) {
      return 1; // Engineering Analysis
    }
    return undefined;
  };

  const handleViewMoc = (row: MocRow) => {
    navigate("/management-of-change/create", {
      state: {
        moc: { mocId: row.mocId, title: row.title, asset: row.asset },
        mode: "view",
        // Open the right section based on how far the workflow has progressed.
        initialStep: getInitialStepForStage(row.workflowStage)
      }
    });
  };

  const handleEditMoc = (row: MocRow) => {
    navigate("/management-of-change/create", {
      state: {
        moc: { mocId: row.mocId, title: row.title, asset: row.asset },
        mode: "edit",
        // Open the right section based on how far the workflow has progressed.
        initialStep: getInitialStepForStage(row.workflowStage)
      }
    });
  };

  // Download is available only once the MOC has reached the completed stage.
  const isReportDownloadable = (row: MocRow): boolean => {
    const normalized = (row.workflowStage || "")
      .toUpperCase()
      .replace(/\s+/g, "_");
    return normalized.includes("MOC_COMPLETED");
  };

  // Editing is blocked once the workflow has progressed to analysis completed,
  // AI assessment completed, or MOC completed.
  const isEditable = (row: MocRow): boolean => {
    const normalized = (row.workflowStage || "")
      .toUpperCase()
      .replace(/\s+/g, "_");
    return !(
      normalized.includes("ANALYSIS_COMPLETED") ||
      normalized.includes("ASSESSMENT_COMPLETED") ||
      normalized.includes("MOC_COMPLETED")
    );
  };

  const handleDownloadMoc = async (row: MocRow) => {
    try {
      const details = await getMocDocumentAccessDetails(row.mocId).unwrap();
      if (!details?.sasUrl) {
        ToastMessageUtil.show(
          "No report is available yet for this MOC.",
          ToastVariant.WARNING
        );
        return;
      }
      const link = document.createElement("a");
      link.href = details.sasUrl;
      link.download = details.documentName || row.mocId || "moc-report";
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Failed to download MOC report:", error);
      ToastMessageUtil.show(
        "We couldn't download the report. Please try again.",
        ToastVariant.ERROR
      );
    }
  };

  return (
    <div className="moc-container">
      {/* Breadcrumb */}
      <nav className="moc-breadcrumb">
        <Link to="/home" className="breadcrumb-link">
          Home
        </Link>
        <span className="breadcrumb-separator">&gt;</span>
        <span className="breadcrumb-current">MOC</span>
      </nav>

      {/* Page header */}
      <div className="moc-page-header">
        <div className="moc-page-header__text">
          <h1 className="moc-page-title">Management of Change</h1>
          <p className="moc-page-description">
            Initate,assess,and track engineering changes to ensure safe and
            compliant implementation.
          </p>
        </div>
        <div className="moc-page-header__actions">
          <Link
            to="/management-of-change/create"
            className="moc-btn moc-btn--primary"
          >
            <AddIcon fontSize="small" />
            <span>Create New MOC</span>
          </Link>
        </div>
      </div>

      {/* KPI row */}
      <section className="moc-kpis">
        {kpis.map((kpi) => (
          <div className="moc-kpi-card" key={kpi.id}>
            <span className="moc-kpi-card__label">{kpi.label}</span>
            <span
              className={`moc-kpi-card__value moc-kpi-card__value--${kpi.accent}`}
            >
              {kpi.value}
            </span>
          </div>
        ))}
      </section>

      {/* Data table */}
      <section className="moc-table-card">
        <div className="moc-table-card__header">
          <h3 className="moc-table-card__title">Engineering Change Logs</h3>
          <div className="moc-table-card__tools">
            <button
              type="button"
              className="moc-btn moc-btn--secondary"
              onClick={() => refetch()}
              disabled={isFetching}
              title="Refresh the MOC list to see the latest workflow status"
            >
              <RefreshIcon
                fontSize="small"
                className={isFetching ? "moc-refresh-spin" : undefined}
              />
              <span>{isFetching ? "Refreshing..." : "Refresh"}</span>
            </button>
            <div className="moc-search-wrapper" ref={searchRef}>
              <div className="moc-search">
                <SearchIcon className="moc-search__icon" fontSize="small" />
                <input
                  type="text"
                  className="moc-search__input"
                  placeholder="Search by MOC ID..."
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value);
                    setShowSuggestions(true);
                  }}
                  onFocus={() => setShowSuggestions(true)}
                />
                {search && (
                  <button
                    type="button"
                    className="moc-search__clear"
                    aria-label="Clear search"
                    onClick={handleClearSearch}
                  >
                    <CloseIcon fontSize="small" />
                  </button>
                )}
              </div>
              {showSuggestions && (
                <ul className="moc-search__suggestions">
                  {mocIdSuggestions.length === 0 ? (
                    <li className="moc-search__suggestion moc-search__suggestion--empty">
                      No matching MOC ID
                    </li>
                  ) : (
                    mocIdSuggestions.map((row) => (
                      <li key={row.id}>
                        <button
                          type="button"
                          className="moc-search__suggestion"
                          onClick={() => handleSelectMocId(row.mocId)}
                        >
                          <span className="moc-search__suggestion-id">
                            {row.mocId}
                          </span>
                          <span className="moc-search__suggestion-title">
                            {row.title}
                          </span>
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              )}
            </div>
          </div>
        </div>

        <div className="moc-table-scroll">
          <table className="moc-table">
            <thead>
              <tr>
                {MOC_TABLE_COLUMNS.map((column) => (
                  <th
                    key={column}
                    className={
                      column === "Title" ? "moc-table__title" : undefined
                    }
                  >
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td
                    colSpan={MOC_TABLE_COLUMNS.length}
                    className="moc-table__muted"
                  >
                    Loading MOCs...
                  </td>
                </tr>
              )}
              {!isLoading && filteredRows.length === 0 && (
                <tr>
                  <td
                    colSpan={MOC_TABLE_COLUMNS.length}
                    className="moc-table__muted"
                  >
                    No MOCs found.
                  </td>
                </tr>
              )}
              {!isLoading &&
                paginatedRows.map((row) => (
                  <tr
                    key={row.id}
                    className={
                      row.priority === "HIGH" ? "moc-table__row--highlight" : ""
                    }
                  >
                    <td className="moc-table__moc-id">
                      <button
                        type="button"
                        className="moc-table__moc-id-link"
                        onClick={() => setSelectedRow(row)}
                      >
                        {row.mocId}
                      </button>
                    </td>
                    <td className="moc-table__title">{row.title}</td>
                    <td>{row.asset}</td>
                    <td>{row.owner}</td>
                    <td>{row.stage}</td>
                    <td>
                      <span
                        className={`moc-priority moc-priority--${row.priority.toLowerCase()}`}
                      >
                        {row.priority}
                      </span>
                    </td>
                    <td>{row.finImpact}</td>
                    <td>{row.schImpact}</td>
                    <td className="moc-table__muted">{row.lastUpdated}</td>
                    <td>
                      <div className="moc-actions">
                        <button
                          type="button"
                          className="moc-action-btn moc-action-btn--view"
                          aria-label="View"
                          onClick={() => handleViewMoc(row)}
                        >
                          <VisibilityOutlined fontSize="small" />
                        </button>
                        <button
                          type="button"
                          className={`moc-action-btn moc-action-btn--edit${
                            isEditable(row) ? "" : " moc-action-btn--disabled"
                          }`}
                          aria-label="Edit"
                          aria-disabled={!isEditable(row)}
                          title={
                            isEditable(row)
                              ? "Edit"
                              : "Editing is disabled once the analysis is completed"
                          }
                          onClick={() => isEditable(row) && handleEditMoc(row)}
                        >
                          <EditOutlined fontSize="small" />
                        </button>
                        <button
                          type="button"
                          className={`moc-action-btn moc-action-btn--download${
                            isReportDownloadable(row)
                              ? ""
                              : " moc-action-btn--disabled"
                          }`}
                          aria-label="Download"
                          aria-disabled={!isReportDownloadable(row)}
                          title={
                            isReportDownloadable(row)
                              ? "Download report"
                              : "Download available only after AI Assessment is completed"
                          }
                          onClick={() =>
                            isReportDownloadable(row) && handleDownloadMoc(row)
                          }
                        >
                          <FileDownloadOutlined fontSize="small" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {/* Footer / pagination */}
        <div className="moc-table-footer">
          <span className="moc-table-footer__info">
            Showing {rangeStart} to {rangeEnd} of {filteredRows.length} MOCs
          </span>
          <div className="moc-pagination">
            <button
              type="button"
              className="moc-page-btn"
              aria-label="Previous"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              &lt;
            </button>
            {pageNumbers.map((page) => (
              <button
                key={page}
                type="button"
                className={`moc-page-btn${
                  page === currentPage ? " moc-page-btn--active" : ""
                }`}
                onClick={() => goToPage(page)}
              >
                {page}
              </button>
            ))}
            <button
              type="button"
              className="moc-page-btn"
              aria-label="Next"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              &gt;
            </button>
          </div>
        </div>
      </section>

      {selectedRow && (
        <MocDetailDrawer
          row={selectedRow}
          onClose={() => setSelectedRow(null)}
        />
      )}
    </div>
  );
};

export default ManagementOfChange;
